import { HubCrossLinks } from '../../../components/hub/HubCrossLinks';
import type { HubCrossLink } from '../../../types/hub-cross-link';
import { buildEmployersInCityLink, buildHubIndexLinks, hubLinksHeading } from '../../../utils/seo/hub-index-links';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Briefcase, Handshake } from '@phosphor-icons/react';
import { JcCard } from '../../../components/jc/JcCard';
import { BrandPanel } from '../../../components/jc/BrandPanel';
import { TrendIndicator, type TrendDirection } from '../../../components/jc/TrendIndicator';
import { isNetworkWorthy } from '../../../utils/best-companies/is-network-worthy';
import type { AlgoliaCompanyRecord } from '../../../types/company-search-record';
import { EmployerScoreCard } from '../../../components/EmployerScoreCard';
import { CompanyCommunityInsights, CompanySiteSources, CompanyFactsCard, OwnedBrandsCard, CommonBenefitsCard } from '../../../components/company-profile';
import { CompanyLogo } from '../../../components/CompanyLogo';
import { formatLastSeenLabel } from '../../../utils/data/format-job-card-labels';
import { adjustBrandColor } from '../../../utils/brand-color/adjust-brand-color';
import { HitCompactV2View } from '../../../components/Hit/HitCompactV2View';
import { getSinglePrioritizedLocation } from '../../../components/location/utils/locationUtils';
import { resolveSalaryInferenceExplanationKind } from '../../../utils/job-detail/resolve-salary-inference-explanation-kind';
import { shouldLinkCompanyProfile } from '../../../utils/company/should-link-company-profile';
import { companyProfileUrlPath } from '../../../utils/routing/company-profile-url-path';
import type { CompanyOpenRoleHit } from '../../../utils/company/fetch-company-open-roles';
import type { DisplayJob } from '../../../types';
// Employer analytics charts (shared with job detail company section, single implementation).
import {
  MonthlyHiringChart,
  StarburstChart,
  MoneyGraph,
} from '../../job/@jobId/@seoSlug/components/CompanyCharts';
import { getIndexName } from '../../../constants';
import {
  algoliaJobHitHasStableObjectId,
  normalisedAlgoliaJobObjectId,
} from '../../../utils/company/normalised-search-job-object-id';
import { generateJobUrl, toRelativePath, type Locale } from '../../../utils/routing/generate-url';
import { BASE_URL, LOCALIZED_PATHS, JOBS_LISTING_SEGMENT } from '../../../utils/constants/url-constants';
import { generateUrlFromIndexUiState } from '../../../utils/search-router/url-handling';
import { toSafeIntlStringValue } from '../../../utils/intl/to-safe-intl-string-value';
import { stripCompanyLegalSuffixForTitle } from '../../../utils/seo/build-job-listing-meta-title';
import { buildCompanyProfileDataTables } from '../../../utils/company/build-company-profile-data-tables';
import { buildCompanySeoNarrative } from '../../../utils/company/build-company-seo-narrative';
import { getCompanyProfileUiCopy, type CompanyProfileUiCopy } from '../../../utils/company/company-profile-ui-copy';
import { careersPageUrlPath } from '../../../utils/routing/careers-page-url-path';
import { CompanyProfileSeoTables } from '../../../components/company-profile/CompanyProfileSeoTables';
import { BreadcrumbNavigation } from '../../../components/BreadcrumbNavigation';
import { buildFullCompanyAddress, formatPostalAddressLine } from '../../../utils/geocoding/match-job-location-with-company-address';
import { buildCompanyLocationMapEntries } from '../../../utils/company/build-company-location-map-entries';

// Lazy-loaded because it pulls in the entire mapbox-gl dependency (~700kB JS + 5kB CSS
// asset), most company pages have no coordinates and never mount this.
const CompanyLocationMap = React.lazy(() =>
  import('../../../components/CompanyLocationMap').then((m) => ({ default: m.CompanyLocationMap })),
);
import type { CompanyProfileLocale } from '../../../utils/seo/company-profile-alternate-urls';
import { usePageContext } from 'vike-react/usePageContext';
import { useCompanyProfilePageData } from '../../../hooks/useCompanyProfilePageData';
import { resolveCompanyProfileRouteSlug } from '../../../utils/company/resolve-company-profile-route-slug';
import { EngagementStrip } from '../../../components/engagement/EngagementStrip';
import { filterCompanyAboutForPageLocale } from '../../../utils/text/is-likely-finnish-prose';
import { resolveRegistryMainBusinessLineForPageLocale } from '../../../utils/company/resolve-registry-main-business-line-for-page-locale';
import {
  resolveJobUrlCompany,
  resolveJobUrlLocation,
  resolveJobUrlRemoteness,
  resolveJobUrlTitle,
} from '../../../utils/routing/job-url-input';
import { trackEvent, trackJobClick } from '../../../utils/analytics/analytics';
import { CompanyFollowButton } from '../../../components/company/CompanyFollowButton';
import { useAppSelector } from '../../../redux/hooks';
import { useUserState } from '../../../query/UserProvider';
import { resolveJobMatchScore } from '../../../utils/job-match/resolve-job-match-score';
import { buildTopRoleLinks, resolveTopLocationLink } from '../../../utils/company/build-company-top-links';
import { Msg } from '../../../i18n-review/client/Msg';

function jobsListingPathForLocale(locale: string): string {
  const l = locale === 'fi' ? 'fi' : locale === 'sv' ? 'sv' : 'en';
  return `/${l}/${JOBS_LISTING_SEGMENT[l]}`;
}

const PAGE_CLASS = 'min-h-screen bg-jc-page';
const H2_CLASS = 'font-display text-base font-bold text-jc-ink';
const BODY_CLASS = 'text-sm text-jc-ink-secondary';
const MUTED_CLASS = 'text-sm text-jc-muted';
const LINK_CLASS = 'text-jc-accent hover:text-jc-cta-hover';

const TREND_DIRECTION: Record<string, TrendDirection> = {
  growing: 'up',
  declining: 'down',
  stable: 'flat',
};

// ── Types ─────────────────────────────────────────────────────────────────────

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatSalary(min?: number, max?: number): string | null {
  if (!min && !max) return null;
  const fmt = (n: number) => `€${Math.round(n / 100) * 100}`.replace(/(\d)(?=(\d{3})+$)/g, '$1,');
  if (min && max) {
    if (min === max) return `${fmt(min)}/mo`;
    return `${fmt(min)} – ${fmt(max)}/mo`;
  }
  if (min) return `from ${fmt(min)}/mo`;
  if (max) return `up to ${fmt(max)}/mo`;
  return null;
}

function trendLabel(trend: string | undefined, copy: ReturnType<typeof getCompanyProfileUiCopy>): string | null {
  if (trend === 'growing') return copy.trendGrowing;
  if (trend === 'declining') return copy.trendDeclining;
  if (trend === 'stable') return copy.trendStable;
  // No hiringTrend yet (company not scored), nothing accurate to show.
  return null;
}

// ── Sub-components ────────────────────────────────────────────────────────────

/**
 * Hero "about" text, clamped to 4 lines by default (some companies' descriptions run 1000+
 * chars and would otherwise dominate the hero). Only shows a Read more/less toggle when the
 * text actually overflows the clamp, measured post-mount so SSR always renders the clamped
 * state (no hydration mismatch) and short descriptions never show a no-op toggle.
 */
function CompanyAboutText({ text, copy }: { text: string; copy: { readMore: string; readLess: string } }) {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setCanExpand(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  return (
    <div className="mt-3">
      <p
        ref={ref}
        className={`leading-relaxed text-[15px] opacity-90 ${expanded ? '' : 'line-clamp-4'}`}
      >
        {text}
      </p>
      {canExpand && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[13px] font-semibold underline-offset-2 hover:underline"
        >
          {expanded ? copy.readLess : copy.readMore}
        </button>
      )}
    </div>
  );
}

/** Rendered inside the brand-color hero panel, pill chips on a translucent white fill. */
function SocialLinks({ company }: { company: AlgoliaCompanyRecord }) {
  const links = [
    { href: company.linkedinUrl, label: 'LinkedIn', external: true },
    { href: company.youtubeUrl, label: 'YouTube', external: true },
    { href: company.facebookUrl, label: 'Facebook', external: true },
    { href: company.instagramUrl, label: 'Instagram', external: true },
    { href: company.tiktokUrl, label: 'TikTok', external: true },
  ].filter(l => l.href);

  if (links.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3.5">
      {links.map((l, i) => (
        <a
          key={l.label}
          href={l.href!}
          target={l.external ? '_blank' : undefined}
          rel={l.external ? 'noopener noreferrer' : undefined}
          className={
            i === 0
              ? 'rounded-full bg-white px-4 py-1.5 text-xs font-bold text-[#16150F] transition-colors hover:bg-white/90'
              : 'rounded-full border border-white/25 bg-white/12 px-3 py-1.5 text-xs transition-colors hover:bg-white/22'
          }
        >
          {l.label}
        </a>
      ))}
    </div>
  );
}

function SalaryBand({
  company,
  copy,
  locale,
}: {
  company: AlgoliaCompanyRecord;
  copy: ReturnType<typeof getCompanyProfileUiCopy>;
  locale: string;
}) {
  const { medianSalaryMin, medianSalaryMax, salaryPercentile25, salaryPercentile75, salarySampleSize } = company;
  if (!medianSalaryMin && !medianSalaryMax) return null;
  const loc = ['en', 'fi', 'sv'].includes(locale) ? locale : 'en';
  // Link to the hub, not a guessed /salary/{role} slug: role pages 404 when the national
  // aggregate has no data for that exact title, and there's no cheap way to check that from
  // here (the per-role index lives in a DynamoDB scan meant for backoffice use, not per-request).
  const salaryHubHref = `/${loc}/${LOCALIZED_PATHS[loc].salary}`;

  const median = formatSalary(medianSalaryMin, medianSalaryMax);
  const medianMid =
    medianSalaryMin != null && medianSalaryMax != null
      ? (medianSalaryMin + medianSalaryMax) / 2
      : medianSalaryMin ?? medianSalaryMax ?? null;
  const hasBand = salaryPercentile25 != null && salaryPercentile75 != null;
  const barMax = hasBand ? salaryPercentile75! * 1.15 : null;
  const toPercent = (v: number) => `${Math.min(100, Math.max(0, (v / (barMax ?? 1)) * 100)).toFixed(1)}%`;
  const fmtEur = (n: number) => `€${Math.round(n).toLocaleString('en-FI')}`;

  return (
    <JcCard className="p-4 sm:p-5">
      <h2 className={`${H2_CLASS} mb-3`}>{copy.salaryDataHeading}</h2>

      {median && (
        <p className="mb-1">
          <span className="font-display text-2xl font-bold tabular-nums text-jc-ink">{median}</span>
          <span className="ml-1.5 text-xs font-semibold uppercase tracking-wide text-jc-muted">
            {copy.monthlyLabel}
          </span>
        </p>
      )}

      {hasBand && barMax && medianMid != null && (
        <div className="mt-3">
          <div className="relative h-2.5 overflow-hidden rounded-full bg-jc-elevated">
            <div
              className="absolute h-full rounded-full bg-gradient-to-r from-jc-accent-soft to-jc-accent"
              style={{
                left: toPercent(salaryPercentile25!),
                width: `calc(${toPercent(salaryPercentile75!)} - ${toPercent(salaryPercentile25!)})`,
              }}
            />
            <div
              className="absolute -top-0.5 h-3.5 w-[3px] rounded-full bg-jc-ink dark:bg-jc-page"
              style={{ left: toPercent(medianMid) }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[11px] font-medium tabular-nums text-jc-muted">
            <span>{copy.middle50Label}</span>
            <span>
              {fmtEur(salaryPercentile25!)} – {fmtEur(salaryPercentile75!)}
            </span>
          </div>
        </div>
      )}

      {salarySampleSize != null && (
        <p className="mt-3 text-xs text-jc-muted">
          {copy.basedOnListings} {salarySampleSize}{' '}
          {salarySampleSize !== 1 ? copy.jobListingPlural : copy.jobListingSingular}
        </p>
      )}

      <a
        href={salaryHubHref}
        className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-jc-cta hover:underline"
      >
        {copy.compareSalariesCta}
        <span aria-hidden>→</span>
      </a>
    </JcCard>
  );
}

/**
 * Open roles list, same row chrome as job detail `SimilarJobsSection` (jc-* tokens, logo,
 * salary chip). `jobs` comes from +data.server.ts (server-side Typesense search), so this
 * content is in the initial crawl rather than only reachable after a client-side fetch.
 */
const OPEN_ROLES_VISIBLE_CAP = 6;

function CompanyOpenRolesPanel({
  companyId,
  companyDisplayName,
  companyURL,
  linkedinUrl,
  locale,
  brandColour,
  seeAllHref,
  copy,
  jobs,
}: {
  companyId: string;
  companyDisplayName: string;
  companyURL?: string;
  linkedinUrl?: string;
  locale: string;
  brandColour?: string;
  seeAllHref: string;
  copy: Pick<
    CompanyProfileUiCopy,
    | 'openRolesHeading'
    | 'openRolesSub'
    | 'seeAllJobs'
    | 'moreRolesLink'
    | 'noOpenRoles'
    | 'noOpenRolesNetworkPrompt'
    | 'website'
    | 'connectOnLinkedIn'
    | 'preferDirectContact'
  >;
  jobs: CompanyOpenRoleHit[];
}) {
  const jobLinkLocale = (['en', 'fi', 'sv'].includes(locale) ? locale : 'en') as Locale;
  const origin = typeof window !== 'undefined' ? window.location.origin : BASE_URL;
  const adjustedBrand = adjustBrandColor(brandColour);

  const canLinkCompanyProfile = shouldLinkCompanyProfile({ companyId, isTyomarkkinatori: false });
  const companyProfileHref = canLinkCompanyProfile
    ? companyProfileUrlPath(jobLinkLocale, companyId)
    : undefined;

  const userProfile = useAppSelector((state) => state.jobForm);
  const comparisonPreferences = useAppSelector((state) => state.comparisonPreferences);
  const savedSearches = useAppSelector((state) => state.recentSearches?.savedSearches ?? []);
  // TEC-404: `userStateData !== undefined` replaces the old Redux `userState.hydratedFromSSR`
  // flag, same replacement useHitShared.ts/job/@jobId/@seoSlug/+Page.tsx already use.
  const { data: userStateData } = useUserState();
  const isHydrated = userStateData !== undefined || typeof window !== 'undefined';

  const jobsWithIds = jobs.filter((job) => algoliaJobHitHasStableObjectId(job));
  const visibleJobs = jobsWithIds.slice(0, OPEN_ROLES_VISIBLE_CAP);
  const hiddenCount = jobsWithIds.length - visibleJobs.length;

  const cards = visibleJobs.flatMap((job, index) => {
    const oid = normalisedAlgoliaJobObjectId(job.objectID);
    let jobHref: string;
    try {
      jobHref = toRelativePath(
        generateJobUrl({
          baseUrl: origin,
          jobId: oid,
          locale: jobLinkLocale,
          company: resolveJobUrlCompany(job, companyId),
          title: resolveJobUrlTitle(job),
          location: resolveJobUrlLocation(job),
          remoteness: resolveJobUrlRemoteness(job),
        }),
      );
    } catch {
      return [];
    }
    const jobTitle = (job.normalisedTitle || job.title || '').trim();
    const rawTitleSubtitle = job.title && job.title !== job.normalisedTitle ? job.title : undefined;
    const singleLocation = getSinglePrioritizedLocation(job as unknown as DisplayJob, undefined);
    const salaryInferenceExplanationKind = resolveSalaryInferenceExplanationKind({
      salaryIsEstimate: job.salaryIsEstimate,
      salaryRangeMin: job.salaryRangeMin,
      salaryRangeMax: job.salaryRangeMax,
      salaryDetails: job.salaryDetails,
    });
    const resolvedMatch = resolveJobMatchScore({
      job: job as unknown as DisplayJob,
      userProfile,
      comparisonPreferences,
      savedSearches,
      isHydrated,
    });
    const matchScoreData = resolvedMatch.breakdown;
    const matchScore = matchScoreData?.overall ?? null;

    const handleJobLinkClick = (e: React.MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor || anchor.getAttribute('href') !== jobHref) return;
      trackJobClick({
        jobId: oid,
        jobTitle,
        companyId: job.companyId ?? companyId,
        source: 'company_profile',
        locale: jobLinkLocale,
      });
    };

    return [
      <div key={oid} onClickCapture={handleJobLinkClick}>
        <HitCompactV2View
          articleId={`job-${oid}`}
          titleId={`job-title-${oid}`}
          jobTitle={jobTitle}
          jobUrl={jobHref}
          companyId={companyId}
          companyNameDisplay={companyId}
          isTyomarkkinatori={false}
          companyProfileUrl={companyProfileHref}
          index={index}
          brandColour={brandColour}
          salaryLabel=""
          salaryRangeMin={job.salaryRangeMin ?? null}
          salaryRangeMax={job.salaryRangeMax ?? null}
          salaryIsEstimate={Boolean(job.salaryIsEstimate)}
          salaryInferenceExplanationKind={salaryInferenceExplanationKind}
          lastSeenLabel={formatLastSeenLabel(job.postedDate, locale)}
          rawTitleSubtitle={rawTitleSubtitle}
          locale={locale}
          singleLocation={singleLocation}
          remoteness={(job.remoteness ?? '').trim()}
          jobType={(job.jobType ?? '').trim()}
          normalisedRoleLevel={(job.normalisedRoleLevel ?? '').trim()}
          requiredLanguages={job.requiredLanguages ?? []}
          matchScore={matchScore}
          matchScoreData={matchScoreData}
        />
      </div>,
    ];
  });

  return (
    <section
      className="rounded-[24px] border border-jc-card-border bg-jc-surface p-4 shadow-[0_1px_3px_rgba(22,21,15,0.08)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.35)] sm:p-5"
      aria-labelledby="company-open-roles-heading"
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl"
            style={{ background: adjustedBrand.panel, color: adjustedBrand.on }}
            aria-hidden
          >
            <Briefcase className="h-4 w-4" strokeWidth={2} weight="fill" />
          </div>
          <h2
            id="company-open-roles-heading"
            className="font-display text-lg font-bold tracking-tight text-jc-ink"
          >
            {copy.openRolesHeading}
          </h2>
        </div>
        <a href={seeAllHref} className="hit-card-v2__cta">
          {copy.seeAllJobs}
        </a>
      </div>
      <p className="mb-3 text-[11px] leading-snug text-jc-muted">{copy.openRolesSub}</p>

      {cards.length === 0 ? (
        <div className="rounded-xl border border-dashed border-jc-border bg-jc-page/50 p-3.5">
          <p className="text-[13px] text-jc-ink">{copy.noOpenRoles}</p>
          {(companyURL || linkedinUrl) ? (
            <>
              <p className="mt-1.5 text-[12px] leading-snug text-jc-muted">
                {copy.noOpenRolesNetworkPrompt.replace('{company}', companyDisplayName)}
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {companyURL ? (
                  <a
                    href={companyURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-full border border-jc-accent/30 bg-jc-accent-muted/45 px-3 py-1.5 text-[12px] font-semibold text-jc-cta transition-colors hover:bg-jc-accent-muted/70"
                  >
                    {copy.website}
                  </a>
                ) : null}
                {linkedinUrl ? (
                  <a
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-full border border-jc-border bg-jc-surface px-3 py-1.5 text-[12px] font-semibold text-jc-ink transition-colors hover:border-jc-accent-soft"
                  >
                    {copy.connectOnLinkedIn}
                  </a>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2.5">{cards}</div>
          {hiddenCount > 0 ? (
            <a
              href={seeAllHref}
              className="mt-2.5 block text-center text-[12px] font-semibold text-jc-cta hover:underline"
            >
              {copy.moreRolesLink.replace('{count}', String(hiddenCount))}
            </a>
          ) : null}
          {companyURL || linkedinUrl ? (
            <p className="mt-3 text-[11px] text-jc-muted">
              {copy.preferDirectContact}{' '}
              {companyURL ? (
                <a href={companyURL} target="_blank" rel="noopener noreferrer" className="font-semibold text-jc-cta hover:underline">
                  {copy.website}
                </a>
              ) : null}
              {companyURL && linkedinUrl ? ' · ' : null}
              {linkedinUrl ? (
                <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-jc-cta hover:underline">
                  {copy.connectOnLinkedIn}
                </a>
              ) : null}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/** Alias for older call sites / merges, same component as `CompanyOpenRolesPanel`. */
const LiveJobList = CompanyOpenRolesPanel;

const OPEN_APPLICATION_GUIDE_HREF: Record<string, string> = {
  fi: '/fi/tyoelama/tyonhakuvelvoite',
  sv: '/sv/arbetsliv/skyldighet-att-soka-arbete',
  en: '/en/resources/job-search-obligation-finland',
};

/**
 * Always-shown open-application card (distinct from CompanyOpenRolesPanel's zero-results
 * prompt): explains, concretely, how the follow button above and the free My Jobs tracker
 * make sending and keeping track of an open application easier, whether or not a role is
 * currently listed. Targets "{company} avoin hakemus"-shaped search intent on the same URL
 * that already ranks for "{company} avoimet työpaikat", rather than a separate thin page.
 */
function CompanyOpenApplicationCard({
  companyDisplayName,
  locale,
  copy,
}: {
  companyDisplayName: string;
  locale: string;
  copy: Pick<
    CompanyProfileUiCopy,
    | 'openApplicationHeading'
    | 'openApplicationIntro'
    | 'openApplicationFollowStep'
    | 'openApplicationTrackStep'
    | 'openApplicationTrackCta'
    | 'openApplicationGuideCta'
  >;
}) {
  const guideHref = OPEN_APPLICATION_GUIDE_HREF[locale] ?? OPEN_APPLICATION_GUIDE_HREF.en;

  return (
    <JcCard className="mt-4 p-4 sm:p-5">
      <h2 className={H2_CLASS}>{copy.openApplicationHeading}</h2>
      <p className={`${BODY_CLASS} mt-2`}>
        {copy.openApplicationIntro.replace('{company}', companyDisplayName)}
      </p>
      <ul className={`${BODY_CLASS} mt-3 list-disc space-y-1.5 pl-4`}>
        <li>{copy.openApplicationFollowStep.replace('{company}', companyDisplayName)}</li>
        <li>{copy.openApplicationTrackStep}</li>
      </ul>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[12px] font-semibold">
        <a href="/tools" className={LINK_CLASS}>
          {copy.openApplicationTrackCta} →
        </a>
        <a href={guideHref} className={LINK_CLASS}>
          {copy.openApplicationGuideCta} →
        </a>
      </div>
    </JcCard>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CompanyPage() {
  const { data, isAwaitingData, isNotFound, notFoundReason } = useCompanyProfilePageData();
  const pageContext = usePageContext();

  const viewedCompanyId = data?.company?.objectID;
  useEffect(() => {
    if (!viewedCompanyId || !data) return;
    trackEvent({
      name: 'company_view',
      params: {
        company_id: viewedCompanyId,
        company_name:
          toSafeIntlStringValue(data.company.companyName, data.locale).trim() || undefined,
        industry: data.industryLabel ?? undefined,
        locale: data.locale,
      },
    });
    // Fire once per company profile, not on every data refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedCompanyId]);

  if (isNotFound) {
    const loc = (pageContext?.locale as string) === 'fi' ? 'fi' : (pageContext?.locale as string) === 'sv' ? 'sv' : 'en';
    const employersHref = `/${loc}/${LOCALIZED_PATHS[loc].bestCompanies}`;
    const copy = getCompanyProfileUiCopy(loc);
    const notFoundTitle =
      notFoundReason === 'no_algolia_profile'
        ? copy.notFoundProfilePendingTitle
        : notFoundReason === 'no_companies_row'
          ? copy.notFoundOutsideCatalogTitle
          : copy.notFoundTitle;
    const notFoundDescription =
      notFoundReason === 'no_algolia_profile'
        ? copy.notFoundProfilePendingDescription
        : notFoundReason === 'no_companies_row'
          ? copy.notFoundOutsideCatalogDescription
          : copy.notFoundDescription;
    return (
      <div className={PAGE_CLASS}>
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        <BreadcrumbNavigation
          items={[
            { label: copy.breadcrumbCompanies, href: employersHref },
            { label: '—' },
          ]}
          className="mb-4 px-0 sm:px-0"
        />
        <JcCard className="max-w-xl p-4 sm:p-5">
          <h1 className="font-display text-2xl font-bold text-jc-ink mb-2">
            {notFoundTitle}
          </h1>
          <p className={BODY_CLASS}>
            {notFoundDescription}
          </p>
          <a href={employersHref} className={`${LINK_CLASS} mt-4 inline-block font-medium`}>
            {copy.breadcrumbCompanies}
          </a>
        </JcCard>
      </div>
      </div>
    );
  }

  if (isAwaitingData) {
    return (
      <div className={`${PAGE_CLASS} max-w-5xl mx-auto px-4 py-6 sm:py-8`} aria-busy="true">
        <div className="mb-4 h-4 w-48 animate-pulse rounded-md bg-jc-border" />
        <div className="mb-5 sm:mb-6 rounded-[24px] p-4 sm:p-6 bg-jc-elevated animate-pulse">
          <div className="flex items-start gap-4 sm:gap-5">
            <div className="h-20 w-20 shrink-0 rounded-2xl bg-jc-border/60" />
            <div className="min-w-0 flex-1 space-y-3">
              <div className="h-8 max-w-md rounded-md bg-jc-border/60" />
              <div className="h-4 w-3/4 max-w-lg rounded-md bg-jc-border/50" />
              <div className="h-4 w-2/3 max-w-md rounded-md bg-jc-border/40" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="h-40 animate-pulse rounded-2xl bg-jc-border/40" />
            <div className="h-40 animate-pulse rounded-2xl bg-jc-border/40" />
          </div>
          <div className="h-64 animate-pulse rounded-2xl bg-jc-border/40" />
        </div>
      </div>
    );
  }

  if (!data) {
    if (typeof window !== 'undefined') {
      console.warn('[company-profile-nav] blank render: no data, not loading, not not-found', {
        pathname: window.location.pathname,
        routeCompanyslug: (pageContext?.routeParams as { companyslug?: string } | undefined)?.companyslug ?? null,
        resolvedSlug: resolveCompanyProfileRouteSlug(pageContext),
        hint: 'Enable verbose logs: localStorage.setItem("debug_company_profile_nav","1")',
      });
    }
    return null;
  }
  const { company, locale, industryLabel, openRoles, hasPublishedCareersPage, commonBenefits } = data;
  const loc: CompanyProfileLocale = locale === 'fi' ? 'fi' : locale === 'sv' ? 'sv' : 'en';
  const copy = getCompanyProfileUiCopy(loc);
  // Brand in the H1 and page copy ("Terveystalo"), matching the title and the URL slug; the
  // registered legal name stays in the facts table and the JSON-LD organisation (+Head).
  const companyDisplayName = stripCompanyLegalSuffixForTitle(
    toSafeIntlStringValue(company.companyName, locale).trim() || company.objectID,
  );
  const narrative = buildCompanySeoNarrative(company, companyDisplayName, loc, industryLabel ?? '');
  const seoTables = buildCompanyProfileDataTables(company, copy);
  const companyAboutForPage = filterCompanyAboutForPageLocale(company.about, locale);

  const rawRegistryBusinessLine = resolveRegistryMainBusinessLineForPageLocale(company, locale);
  const registryBusinessLine =
    loc === 'fi'
      ? rawRegistryBusinessLine
      : filterCompanyAboutForPageLocale(rawRegistryBusinessLine, locale);
  const seeAllJobsHref = generateUrlFromIndexUiState(
    { refinementList: { companyId: [company.objectID] }, page: 0 },
    getIndexName(locale),
    jobsListingPathForLocale(locale),
    { forceJobsListingForCompany: true },
  );

  // Internal links to this company's top roles and top hiring location, the roleDistribution/
  // locationDistribution StarburstCharts below visualize the same data but aren't clickable.
  const topRoleLinks = buildTopRoleLinks(company.topRoles, locale);
  // The card also carries the employers-in-city page and the index hubs so profile pages feed
  // the hub graph (they are the bulk of the site's indexable non-job pages).
  const topLocationLink = resolveTopLocationLink(company.locationDistribution, loc);

  const trendText = trendLabel(company.hiringTrend, copy);
  const trendDirection = company.hiringTrend ? TREND_DIRECTION[company.hiringTrend] : undefined;
  const networkWorthy = isNetworkWorthy(company);

  const companyAddressLine = formatPostalAddressLine(
    buildFullCompanyAddress({
      registryStreet: company.registryStreet,
      registryPostCode: company.registryPostCode,
      registryCity: company.registryCity,
    }),
  );
  // TEC-482 follow-up: derived from the gazetteer-resolved companyLocations (each entry's own
  // resolved place carries lat/lng), not the old company._geoloc, that field was set in
  // generateCompanyScores.ts's AlgoliaCompanyRecord but was never actually mapped into the
  // Typesense companies collection, so it was always undefined by the time it reached this page.
  const companyLocationMapEntries = buildCompanyLocationMapEntries(company.companyLocations, loc, companyAddressLine);
  const hasRealCompanyAddress = companyLocationMapEntries.length > 0;

  return (
    <div className={PAGE_CLASS}>
    <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
      <BreadcrumbNavigation
        items={[
          { label: copy.breadcrumbCompanies, href: `/${loc}/${LOCALIZED_PATHS[loc].bestCompanies}` },
          { label: companyDisplayName },
        ]}
        className="mb-4 px-0 sm:px-0"
      />

      {/* Hero */}
      <BrandPanel brandColour={company.brandColour} className="mb-4 rounded-[24px] p-4 shadow-[0_8px_24px_rgba(22,21,15,0.16)] sm:p-6">
        <div className="flex items-start gap-4 sm:gap-5">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white p-2.5 shadow-[0_2px_6px_rgba(0,0,0,0.18)] sm:h-24 sm:w-24">
            <CompanyLogo
              companyName={company.objectID}
              initialsSourceName={companyDisplayName}
              size={80}
              variant="bare"
              isTyomarkkinatori={false}
              loading="eager"
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h1 className="font-display text-2xl font-bold sm:text-3xl">
                {companyDisplayName}
              </h1>
              {trendDirection && trendText ? (
                <TrendIndicator direction={trendDirection} tone="on-brand">
                  {trendText}
                </TrendIndicator>
              ) : null}
              {networkWorthy ? (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-white/22 px-2.5 py-1 text-[11.5px] font-bold"
                  title="High employer score, rarely posts jobs publicly — worth reaching out to directly"
                >
                  <Handshake className="h-3 w-3" weight="fill" />
                  <Msg id="bestCompanies.row.networkWorthy" defaultMessage="Worth networking with" />
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm opacity-85">
              {industryLabel ? <span>{industryLabel}</span> : null}
              {company.registryCity && (
                <span>· {company.registryCity}{company.registryCountry ? `, ${company.registryCountry}` : ''}</span>
              )}
              {(company.companyHeadCountMin || company.companyHeadCountMax) && (
                <span>
                  ·&nbsp;
                  {company.companyHeadCountMin && company.companyHeadCountMax
                    ? `${company.companyHeadCountMin}–${company.companyHeadCountMax} ${copy.employees}`
                    : `${company.companyHeadCountMin ?? company.companyHeadCountMax} ${copy.employees}`}
                </span>
              )}
              {company.foundingYear ? (
                // A stated fact from the company's own pages (CompanySiteProfile.foundingYear)
                // beats the companyAgeYears-derived estimate below, no "est." suffix needed.
                <span>· {copy.foundedYearEst} {company.foundingYear}</span>
              ) : (
                company.companyAgeYears != null && (
                  <span>
                    · {copy.foundedYearEst}{' '}
                    {new Date().getFullYear() - Math.round(company.companyAgeYears)}
                    {copy.foundedYearSuffix}
                  </span>
                )
              )}
              {company.companyURL && (
                <a
                  href={company.companyURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline-offset-2 hover:underline"
                >
                  {copy.website}
                </a>
              )}
              {hasPublishedCareersPage ? (
                // Deliberate cross-link (TEC-488): careers page and profile link to each other
                // instead of canonicalizing, different intents, and this is half of the
                // duplicate-content mitigation.
                <a
                  href={careersPageUrlPath(locale, company.objectID)}
                  className="font-medium underline-offset-2 hover:underline"
                >
                  {copy.careersPage}
                </a>
              ) : null}
            </div>

            {companyAboutForPage ? (
              <>
                <CompanyAboutText
                  text={companyAboutForPage}
                  copy={{ readMore: copy.readMore, readLess: copy.readLess }}
                />
                <CompanySiteSources company={company} />
              </>
            ) : null}

            <SocialLinks company={company} />
          </div>
          <div className="shrink-0 self-start">
            <CompanyFollowButton companyId={company.objectID} companyName={companyDisplayName} onBrand surface="company-profile" />
          </div>
        </div>
      </BrandPanel>

      <div className="grid grid-cols-1 gap-6 mb-6 sm:mb-8 lg:grid-cols-2 lg:items-start lg:gap-8">
        {/* Left: all graphs & company data — single column; right: open roles (lg) */}
        <div className="order-1 min-w-0 space-y-4 sm:space-y-5 lg:order-1">
          {/* All analytics in one column; open roles live in the aside */}
          <div className="space-y-4 sm:space-y-5">
            <EmployerScoreCard company={company} copy={copy} />
            <CompanyFactsCard company={company} />
            <CommonBenefitsCard benefits={commonBenefits} />
            <OwnedBrandsCard company={company} />
            <CompanyCommunityInsights company={company} />
            <SalaryBand company={company} copy={copy} locale={locale} />

            {(company.remotenessDistribution ||
              company.revenue ||
              company.profit ||
              company.profitMargin ||
              (company.companyMonthlyCounts && Object.keys(company.companyMonthlyCounts).length >= 2) ||
              (company.roleDistribution && Object.keys(company.roleDistribution).length > 0) ||
              (company.roleLevelDistribution && Object.keys(company.roleLevelDistribution).length > 0) ||
              (company.locationDistribution && Object.keys(company.locationDistribution).length > 0) ||
              (company.languageRequirements && Object.keys(company.languageRequirements).length > 0)) && (
              <p className="font-display text-xs font-bold uppercase tracking-wide text-jc-muted pt-1 sm:text-sm">
                {copy.hiringInsightsEyebrow}
              </p>
            )}

            {company.remotenessDistribution && (
              <JcCard className="p-4 sm:p-5">
                <StarburstChart
                  data={{
                    Remote: company.remotenessDistribution.remote,
                    Hybrid: company.remotenessDistribution.hybrid,
                    Onsite: company.remotenessDistribution.onsite,
                  }}
                  title={copy.workModelChartTitle}
                  titleKey="remotenessDistribution"
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  density="compact"
                />
              </JcCard>
            )}

            {(company.revenue || company.profit || company.profitMargin) && (
              <JcCard className="p-4 sm:p-5">
                <MoneyGraph
                  revenue={company.revenue}
                  profit={company.profit}
                  profitMargin={company.profitMargin}
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  density="compact"
                />
              </JcCard>
            )}

            {company.companyMonthlyCounts && Object.keys(company.companyMonthlyCounts).length >= 2 && (
              <JcCard className="p-4 sm:p-5">
                <MonthlyHiringChart
                  monthlyCounts={company.companyMonthlyCounts}
                  headcountMin={company.companyHeadCountMin}
                  headcountMax={company.companyHeadCountMax}
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  density="compact"
                />
              </JcCard>
            )}

            {company.roleDistribution && Object.keys(company.roleDistribution).length > 0 && (
              <JcCard className="p-4 sm:p-5">
                <StarburstChart
                  data={company.roleDistribution}
                  title={copy.rolesChartTitle}
                  titleKey="roleDistribution"
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  maxItems={8}
                  density="compact"
                />
              </JcCard>
            )}

            {company.roleLevelDistribution && Object.keys(company.roleLevelDistribution).length > 0 && (
              <JcCard className="p-4 sm:p-5">
                <StarburstChart
                  data={company.roleLevelDistribution}
                  title={copy.roleLevelChartTitle}
                  titleKey="roleLevelDistribution"
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  maxItems={6}
                  density="compact"
                />
              </JcCard>
            )}

            {company.locationDistribution && Object.keys(company.locationDistribution).length > 0 && (
              <JcCard className="p-4 sm:p-5">
                <StarburstChart
                  data={company.locationDistribution}
                  title={copy.locationsChartTitle}
                  titleKey="locationDistribution"
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  maxItems={6}
                  density="compact"
                />
              </JcCard>
            )}

            {(topRoleLinks.length > 0 || topLocationLink) && (
              <JcCard className="p-4 sm:p-5">
                {topRoleLinks.length > 0 && (
                  <>
                    <h2 className={`${H2_CLASS} mb-2`}>{copy.topRolesLinksHeading}</h2>
                    <ul className="flex flex-wrap gap-2">
                      {topRoleLinks.map(({ role, href }) => (
                        <li key={role}>
                          <a href={href} className={`${LINK_CLASS} block rounded-full border border-jc-border px-3 py-1 text-sm`}>
                            {role}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {topLocationLink && (
                  <p className={`${BODY_CLASS} ${topRoleLinks.length > 0 ? 'mt-3' : ''}`}>
                    <a href={topLocationLink.href} className={LINK_CLASS}>
                      {copy.alsoHiringInLabel.replace('{location}', topLocationLink.location)}
                    </a>
                  </p>
                )}
              </JcCard>
            )}

            {/* Always rendered, even when the company has no role/location data: every profile links the hubs. */}
            <JcCard className="p-4 sm:p-5">
              <HubCrossLinks
                variant="chips"
                locale={loc}
                heading={hubLinksHeading(loc)}
                links={[
                  ...((): HubCrossLink[] => {
                    const employersInCity = topLocationLink ? buildEmployersInCityLink(loc, topLocationLink.location) : null;
                    return employersInCity ? [employersInCity] : [];
                  })(),
                  ...buildHubIndexLinks(loc, ['employers', 'roles', 'salary']),
                ]}
              />
            </JcCard>

            {company.languageRequirements && Object.keys(company.languageRequirements).length > 0 && (
              <JcCard className="p-4 sm:p-5">
                <StarburstChart
                  data={company.languageRequirements}
                  title={copy.languageChartTitle}
                  titleKey="languageRequirements"
                  brandColour={company.brandColour}
                  companyName={companyDisplayName}
                  locale={locale}
                  maxItems={6}
                  density="compact"
                />
              </JcCard>
            )}

            {hasRealCompanyAddress && (
              <JcCard className="p-4 sm:p-5">
                <h2 className={`${H2_CLASS} mb-3`}>
                  {copy.locationHeading}
                </h2>
                <Suspense fallback={<div className="w-full h-[400px] bg-jc-page/50 rounded-xl animate-pulse" aria-hidden="true" />}>
                  <CompanyLocationMap
                    locations={companyLocationMapEntries}
                    companyName={companyDisplayName}
                    companyId={company.objectID}
                  />
                </Suspense>
              </JcCard>
            )}

            {company.registryBusinessId && (
              <JcCard className="p-4 sm:p-5">
                <h2 className={`${H2_CLASS} mb-3`}>
                  {copy.companyInfoHeading}
                </h2>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {company.registryBusinessId && (
                    <>
                      <dt className="text-jc-muted">{copy.businessId}</dt>
                      <dd className="font-semibold text-jc-ink">{company.registryBusinessId}</dd>
                    </>
                  )}
                  {company.registryStatus && (
                    <>
                      <dt className="text-jc-muted">{copy.status}</dt>
                      <dd className="font-semibold text-jc-ink">{company.registryStatus}</dd>
                    </>
                  )}
                  {company.registryRegistrationDate && (
                    <>
                      <dt className="text-jc-muted">{copy.founded}</dt>
                      <dd className="font-semibold text-jc-ink">
                        {company.registryRegistrationDate.substring(0, 10)}
                      </dd>
                    </>
                  )}
                  {registryBusinessLine && (
                    <>
                      <dt className="text-jc-muted">{copy.businessLine}</dt>
                      <dd className="col-span-1 font-semibold text-jc-ink">
                        {registryBusinessLine}
                      </dd>
                    </>
                  )}
                </dl>
              </JcCard>
            )}
          </div>
        </div>

        {/* Open roles — right on large screens; first below hero on small screens */}
        <aside className="order-2 min-w-0 lg:order-2 lg:pl-1">
          <LiveJobList
            jobs={openRoles ?? []}
            companyId={company.objectID}
            companyDisplayName={companyDisplayName}
            companyURL={company.companyURL}
            linkedinUrl={company.linkedinUrl}
            locale={locale}
            brandColour={company.brandColour}
            seeAllHref={seeAllJobsHref}
            copy={copy}
          />
          <CompanyOpenApplicationCard
            companyDisplayName={companyDisplayName}
            locale={locale}
            copy={copy}
          />
        </aside>
      </div>

      <details className="mb-6 rounded-2xl border border-jc-border bg-jc-surface px-4 py-3 sm:mb-8 sm:px-5 sm:py-4">
        <summary className="cursor-pointer font-display text-sm font-bold text-jc-ink sm:text-base transition-colors hover:text-jc-accent">
          {copy.summaryHeading}
        </summary>
        <div className="mt-3 max-w-2xl space-y-3 text-sm leading-relaxed text-jc-muted">
          <p>{narrative.lead}</p>
          {narrative.detail ? <p>{narrative.detail}</p> : null}
        </div>
      </details>

      <div className="max-w-6xl mx-auto px-2 sm:px-6 pb-6">
        <EngagementStrip
          feedbackSource="company_profile"
          variant="card"
          locale={locale}
          showReportButton
          feedbackMetadata={{
            companyId: company.objectID,
            companyName: companyDisplayName,
            locale,
          }}
        />
      </div>

      <CompanyProfileSeoTables tables={seoTables} visuallyHiddenHeading={copy.dataTablesHeading} />
    </div>
    </div>
  );
}
